// Removed performance logging for production
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import {
  batchLoadNamespaces,
  preloadCriticalTranslations,
} from "./utils/i18nOptimizer";

// Critical namespaces needed for initial render
const criticalNamespaces = [
  "common",
  "error",
  "signup",
  "alert",
  "placeholder",
  "mainPage",
];

// All namespaces for lazy loading
const allNamespaces = [
  "addMemoDialog",
  "alert",
  "booking",
  "photoType",
  "changePassword",
  "clientManagement",
  "common",
  "company",
  "companyManagement",
  "contract",
  "cancelAlertDialog",
  "dashboard",
  "emailDialog",
  "emailHistoryDialog",
  "enterpriseSignup",
  "error",
  "factoryManagement",
  "inquiry",
  "faq",
  "notice",
  "fileBox",
  "findPassword",
  "itemManagement",
  "linkToAccount",
  "mainPage",
  "myPage",
  "subscriptionPlanSignup",
  "notification",
  "onboarding",
  "registeredLinkToAccount",
  "sendLinkCodeDialog",
  "shareableContractDialog",
  "shareEmail",
  "shipment",
  "sideNav",
  "signup",
  "signupCorp",
  "signupStaff",
  "staffManagement",
  "subscriptionAnnuallyTermination",
  "subscriptionLanding",
  "subscriptionManagement",
  "success",
  "sales",
  "task",
  "terms",
  "temporarySignupCorp",
  "twoFactorAuthentication",
  "warehouseManagement",
  "table",
  "placeholder",
  "status",
  "dispatch",
];

// Performance monitoring disabled for production

// Get detected language early
const detectedLng = localStorage.getItem("i18nextLng") || "en";

// Preload critical translations immediately
preloadCriticalTranslations(detectedLng, criticalNamespaces);

// Initialize i18n with simpler approach - let i18next handle loading
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(HttpApi)
  .init({
    detection: {
      order: ["querystring", "localStorage"],
      lookupLocalStorage: "i18nextLng", // 로컬스토리지 키 이름
    },
    fallbackLng: {
      "ko-KR": ["ko"],
      default: ["en"],
    },
    debug: false,
    ns: criticalNamespaces, // Load only critical namespaces initially
    defaultNS: "common",
    fallbackNS: ["common"],
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      // Enable parallel loading
      allowMultiLoading: true,
      // Request with credentials for better caching
      requestOptions: {
        mode: "cors",
        credentials: "same-origin",
        cache: "force-cache",
      },
    },
    interpolation: {
      escapeValue: false,
    },
    keySeparator: false,
    partialBundledLanguages: true,
    react: {
      useSuspense: false, // Don't suspend on missing translations
      wait: true, // Wait for translations to load before rendering
      bindI18n: "languageChanged loaded added removed", // Re-render on all i18n events
      bindI18nStore: "added removed", // Re-render when resources are added/removed
      transSupportBasicHtmlNodes: true, // Support basic HTML in Trans components
      transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p"], // Keep these HTML nodes
    },
  })
  .then(() => {
    // Emit event after i18n init is complete
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("i18nReady", {
          detail: {
            language: i18n.language,
            namespaces: criticalNamespaces,
            resourcesLoaded: true,
          },
        }),
      );
    }, 100);
  })
  .catch((error) => {
    // Dispatch event anyway to prevent app from hanging
    window.dispatchEvent(
      new CustomEvent("i18nReady", {
        detail: {
          error: error.message,
          fallback: true,
        },
      }),
    );
  });

// Load remaining namespaces with optimized batching
export const loadRemainingNamespaces = async () => {
  const remainingNamespaces = allNamespaces.filter(
    (ns) => !criticalNamespaces.includes(ns),
  );

  if (remainingNamespaces.length > 0) {
    try {
      // Load in batches to avoid overwhelming the browser
      await batchLoadNamespaces(
        (batch) => i18n.loadNamespaces(batch),
        remainingNamespaces,
        10, // Load 10 namespaces at a time
      );
    } catch (error) {
      // Silently handle namespace loading errors
    }
  }
};

// Preload namespaces for specific routes
export const preloadRouteNamespaces = (route: string) => {
  const routeNamespaces: Record<string, string[]> = {
    "/exporter/contract": ["contract", "clientManagement", "companyManagement"],
    "/exporter/booking": ["booking", "shipment", "task"],
    "/exporter/dashboard": ["dashboard", "notification"],
    // Add more route mappings as needed
  };

  const namespaces = routeNamespaces[route];
  if (namespaces) {
    const language = i18n.language || "en";
    namespaces.forEach((ns) => {
      if (!i18n.hasResourceBundle(language, ns)) {
        i18n.loadNamespaces(ns);
      }
    });
  }
};

export default i18n;
